import React from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Copy, Download, Mail, Code2 } from "lucide-react";

const BOILERPLATE = `Plyx (plyx.co.uk) is a social video platform where moments become movements. Users watch and share short videos, go live, join challenges and earn real rewards through watch-to-earn credits and virtual gifts. Built-in one-tap translation makes clips accessible across African and world languages, and creators can sell directly from their videos through AfriMarket — Plyx's integrated marketplace for African fashion, food, beauty and services across the UK, US, Canada and Africa.`;

const EMBED_SNIPPET = `<iframe
  src="https://plyx.co.uk/plyx/embed/VIDEO_ID"
  width="360" height="640"
  style="border:0;border-radius:16px;overflow:hidden"
  allowfullscreen title="Plyx video"></iframe>`;

const FACTS = [
  ["What", "Social video + live streaming + social commerce"],
  ["Tagline", "Where moments become movements"],
  ["Markets", "UK · US · Canada · Africa"],
  ["Marketplace", "AfriMarket — African fashion, food, beauty & services"],
  ["Signature features", "Watch-to-earn rewards, one-tap translation, live gifts, creator shops"],
  ["Platform", "Web + installable mobile app (PWA)"],
];

const ASSETS = [
  { name: "Logo tile (1024×1024)", file: "/press/plyx-logo.png" },
  { name: "Brand banner (wide)", file: "/press/plyx-banner.png" },
  { name: "Social share card", file: "/plyx-og.png" },
];

const copyText = async (text, label) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  } catch {
    window.prompt(`Copy ${label}:`, text);
  }
};

export default function PlyxPress() {
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white pb-28" data-testid="plyx-press-page">
      <div className="max-w-3xl mx-auto px-5 pt-10">
        <img src="/press/plyx-banner.png" alt="Plyx brand banner" className="w-full rounded-2xl border border-white/10 mb-8" />

        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#FFB42D] mb-2">Press kit</p>
        <h1 className="text-4xl sm:text-5xl font-black tracking-tight mb-3">
          Plyx — where moments become <span className="bg-gradient-to-r from-[#FF2D7A] via-[#FFB42D] to-[#00E0C7] bg-clip-text text-transparent">movements</span>
        </h1>
        <p className="text-white/60 text-base mb-10">Everything you need to write about Plyx: boilerplate, facts, logos and embeds.</p>

        <section className="mb-10">
          <h2 className="text-lg font-bold mb-3">Boilerplate</h2>
          <div className="rounded-2xl bg-white/5 border border-white/10 p-5">
            <p className="text-sm leading-relaxed text-white/85" data-testid="plyx-press-boilerplate">{BOILERPLATE}</p>
            <button
              type="button"
              onClick={() => copyText(BOILERPLATE, "Boilerplate")}
              data-testid="plyx-press-copy-boilerplate"
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white text-[#0A0A0A] text-xs font-bold hover:bg-white/90 transition-colors"
            >
              <Copy className="w-3.5 h-3.5" /> Copy boilerplate
            </button>
          </div>
        </section>

        <section className="mb-10">
          <h2 className="text-lg font-bold mb-3">Fast facts</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {FACTS.map(([k, v]) => (
              <div key={k} className="rounded-xl bg-white/5 border border-white/10 p-4">
                <div className="text-[11px] font-bold uppercase tracking-wider text-white/50 mb-1">{k}</div>
                <div className="text-sm text-white/90">{v}</div>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-10">
          <h2 className="text-lg font-bold mb-3">Brand assets</h2>
          <div className="grid sm:grid-cols-3 gap-4">
            {ASSETS.map((a) => (
              <a
                key={a.file}
                href={a.file}
                download
                data-testid={`plyx-press-asset-${a.file.split("/").pop()}`}
                className="group rounded-2xl bg-white/5 border border-white/10 overflow-hidden hover:border-white/30 transition-colors"
              >
                <img src={a.file} alt={a.name} className="w-full h-32 object-cover" />
                <div className="p-3 flex items-center justify-between gap-2">
                  <span className="text-xs text-white/80">{a.name}</span>
                  <Download className="w-4 h-4 text-white/50 group-hover:text-white shrink-0" />
                </div>
              </a>
            ))}
          </div>
        </section>

        <section className="mb-10">
          <h2 className="text-lg font-bold mb-1 inline-flex items-center gap-2"><Code2 className="w-5 h-5 text-[#00E0C7]" /> Embed a Plyx video</h2>
          <p className="text-sm text-white/60 mb-3">
            Any public video can be embedded on your website or blog. Open a video on Plyx and tap
            <span className="text-white font-semibold"> Embed</span> to copy its ready-made code, or use this template:
          </p>
          <div className="rounded-2xl bg-white/5 border border-white/10 p-5">
            <pre className="text-[11px] leading-relaxed text-[#00E0C7] whitespace-pre-wrap break-all" data-testid="plyx-press-embed-snippet">{EMBED_SNIPPET}</pre>
            <button
              type="button"
              onClick={() => copyText(EMBED_SNIPPET, "Embed code")}
              data-testid="plyx-press-copy-embed"
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white text-[#0A0A0A] text-xs font-bold hover:bg-white/90 transition-colors"
            >
              <Copy className="w-3.5 h-3.5" /> Copy embed template
            </button>
          </div>
        </section>

        <section className="mb-10">
          <h2 className="text-lg font-bold mb-3">Press contact</h2>
          <a
            href="mailto:press@plyx.co.uk"
            data-testid="plyx-press-contact"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-[#FF2D7A] via-[#FFB42D] to-[#00E0C7] text-[#0A0A0A] text-sm font-bold"
          >
            <Mail className="w-4 h-4" /> press@plyx.co.uk
          </a>
        </section>

        <Link to="/plyx" className="text-sm text-white/50 hover:text-white underline underline-offset-4">← Back to the feed</Link>
      </div>
    </div>
  );
}
